/**
 * Auth API client — thin wrapper around `axios` that mirrors the
 * process-runner endpoints exactly. Pages should call into
 * `auth.service.ts` rather than this module directly so session-storage
 * side effects stay in one place.
 */

import axios from '../axios'
import { ENV } from '../env'
import type { SignedUserDetails, TenantDetails } from '../session'
import { getRefreshToken } from '../session'
import { AppType, type ForgotPasswordRequest, type LoginRequest, type LoginResponse, type LoginSettingResponse, type OtpMethodInfo, type OtpVerifyResponse, type RefreshResponse } from './types'

type AuthRequestOpts = { skipAuthRedirect?: boolean }

const JSON_HEADERS = { 'Content-Type': 'application/json' }

const UA = () => ENV.ua // e.g. /api/ua  (mylocal) or full URL
const BUYER = () => ENV.buyer

/**
 * Re-shape an axios login error to match process-runner's contract:
 * the caller receives a `LoginResponse`-shaped object with an `.error`
 * field carrying `loginStatus`, `sessionId`, `socialSigninError`, etc.
 * Works for both wrappings the backend produces (top-level fields, or
 * a nested `.data` envelope).
 */
function unwrapLoginError(err: unknown): never {
  const e = err as {
    response?: {
      status?: number
      data?: LoginResponse & { error?: LoginResponse['error'] }
    }
  }
  const body = e?.response?.data
  if (body) {
    const inner = body.data ?? body
    const payload: LoginResponse = {
      error: body.error ?? {
        loginStatus: inner.loginStatus,
        sessionId: inner.sessionId,
        status: inner.status,
      },
    }
    return Promise.reject(payload) as never
  }
  return Promise.reject(err) as never
}

// --------------------------------------------------------------------------
// Login / logout / refresh
// --------------------------------------------------------------------------

export async function login(req: LoginRequest): Promise<LoginResponse> {
  return axios
    .post<LoginResponse>(`${UA()}/auth/login`, req, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
      cache: false,
    })
    .then((res) => {
      // Some endpoints nest the response in `.data` (`{ data: { … } }`);
      // others return at top level. Flatten so callers can read fields
      // directly without checking.
      const body = res.data
      return body.data ?? body
    })
    .catch(unwrapLoginError)
}

export async function logout(sessionId?: string): Promise<string> {
  const url = sessionId
    ? `${BUYER()}/auth/logout/${sessionId}`
    : `${BUYER()}/auth/logout`
  const refreshToken = getRefreshToken()
  return axios
    .post<string>(url, refreshToken || null, {
      headers: { 'Content-Type': 'text/plain' },
      withCredentials: true,
      cache: false,
    })
    .then((res) => res.data)
}

export async function refreshToken(): Promise<RefreshResponse> {
  const refreshTokenValue = getRefreshToken()
  return axios
    .post<RefreshResponse>(`${BUYER()}/auth/refreshToken`, refreshTokenValue || null, {
      headers: { 'Content-Type': 'text/plain' },
      withCredentials: true,
      cache: false,
      disableRetry: true,
    })
    .then((res) => res.data)
}

// --------------------------------------------------------------------------
// OTP
// --------------------------------------------------------------------------

export async function getOtpLoginMethods(
  tenantId: string,
  convId: string,
): Promise<OtpMethodInfo> {
  return axios
    .get<OtpMethodInfo>(
      `${UA()}/comm/otp/${encodeURIComponent(tenantId)}/${convId}/methods`,
      { withCredentials: true },
    )
    .then((res) => res.data)
}

export async function sendOtpCode(
  tenantId: string,
  convId: string,
  method: 'email' | 'mobile',
  locale = 'en',
): Promise<string> {
  return axios
    .get<string>(
      `${UA()}/comm/otp/${encodeURIComponent(tenantId)}/${convId}/queue?method=${method}&locale=${locale}`,
      { withCredentials: true, cache: false },
    )
    .then((res) => res.data)
}

export async function verifyOtp(
  tenantId: string,
  convId: string,
  otp: string,
): Promise<OtpVerifyResponse> {
  return axios
    .get<OtpVerifyResponse>(
      `${UA()}/comm/otp/${encodeURIComponent(tenantId)}/${convId}/verify?otp=${encodeURIComponent(otp)}`,
      { withCredentials: true, cache: false },
    )
    .then((res) => res.data)
}

// --------------------------------------------------------------------------
// Forgot password
// --------------------------------------------------------------------------

export async function forgotPassword(
  req: ForgotPasswordRequest,
): Promise<string> {
  return axios
    .put<string>(`${UA()}/auth/forgotPassword`, req, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
      cache: false,
    })
    .then((res) => res.data)
}

// --------------------------------------------------------------------------
// SSO / login-settings / logo
// --------------------------------------------------------------------------

export async function getLoginSettings(
  tenantId: string,
  targetUri?: string,
  userName?: string,
  connectionName?: string,
): Promise<LoginSettingResponse> {
  const params = new URLSearchParams({
    tenantId,
    appType: AppType.indirect,
  })
  if (targetUri) params.set('targetUri', targetUri)
  if (userName) params.set('username', userName)
  if (connectionName) params.set('connectionName', connectionName)
  return axios
    .get<LoginSettingResponse>(`${UA()}/auth/loginSettings?${params.toString()}`, {
      withCredentials: true,
    })
    .then((res) => res.data)
}

export async function getSsoLoginUrl(
  tenantId: string,
  targetUri: string,
  connection?: string,
): Promise<string> {
  const params = new URLSearchParams({
    tenantId,
    appType: AppType.indirect,
    targetUri,
  })
  if (connection) params.set('connection', connection)
  return axios
    .get<string>(`${UA()}/auth/ssoLoginUrl?${params.toString()}`, {
      withCredentials: true,
    })
    .then((res) => res.data)
}

export async function getLogoUrl(tenantId: string): Promise<string> {
  return axios
    .get<{ url?: string } | string>(
      `${UA()}/auth/logoUrl?tenantId=${encodeURIComponent(tenantId)}`,
      { withCredentials: true },
    )
    .then((res) => {
      const d = res.data as { url?: string } | string
      return typeof d === 'string' ? d : (d?.url ?? '')
    })
    .catch(() => '')
}

// --------------------------------------------------------------------------
// Tenants
// --------------------------------------------------------------------------

// --------------------------------------------------------------------------
// Impersonate / sysadmin assume
// --------------------------------------------------------------------------

export async function impersonate(
  tenantId: string,
  username: string,
  purpose: string,
): Promise<LoginResponse> {
  const params = new URLSearchParams({ purpose, appType: AppType.indirect })
  return axios
    .post<LoginResponse>(
      `${BUYER()}/auth/impersonate/${encodeURIComponent(tenantId)}/${encodeURIComponent(username)}?${params.toString()}`,
      null,
      { withCredentials: true, cache: false },
    )
    .then((res) => res.data)
    .catch(unwrapLoginError)
}

/**
 * `POST /auth/assume/{tenantId}[/{username}]?purpose=&appType=indirect`
 * with `Authorization: ${sysAdminToken}`.
 *
 * Implementation note: uses `fetch()` directly (not the shared `axios`
 * instance) so the request bypasses the axios request interceptor's
 * `isValidSession()` gate — the user landing on this flow has NO
 * session yet, so an axios call would be redirected to `/login`
 * before it ever fires. Mirrors process-runner's
 * `sysAdminLoginApi` exactly.
 */
export async function sysAdminAssume(
  sysAdminToken: string,
  tenantId: string,
  purpose: string,
  username?: string | null,
): Promise<LoginResponse> {
  const params = new URLSearchParams({ purpose, appType: AppType.indirect })
  const path = username
    ? `auth/assume/${encodeURIComponent(tenantId)}/${encodeURIComponent(username)}`
    : `auth/assume/${encodeURIComponent(tenantId)}`
  const url = `${BUYER()}/${path}?${params.toString()}`

  // Match process-runner's `sysAdminLoginApi` exactly: no
  // `credentials: 'include'`. The sysadmin token in `Authorization` is
  // the sole credential here; sending session cookies alongside can
  // make the backend reject the assume call (the cookie identifies a
  // different user than the token is asking to assume).
  return fetch(url, {
    method: 'POST',
    referrerPolicy: 'origin',
    headers: {
      'Content-Type': 'application/json',
      Authorization: sysAdminToken,
    },
  })
    .then(async (resp) => {
      const body = (await resp.json().catch(() => ({}))) as
        | (LoginResponse & { error?: LoginResponse['error'] })
        | LoginResponse
      const flattened = (body as LoginResponse).data ?? (body as LoginResponse)
      if (!resp.ok) {
        const err: LoginResponse = {
          error: (body as { error?: LoginResponse['error'] }).error ?? {
            loginStatus: flattened.loginStatus,
            sessionId: flattened.sessionId,
            status: flattened.status,
          },
        }
        return Promise.reject(err)
      }
      return flattened
    })
}

// --------------------------------------------------------------------------
// Profile (supplier-contact) setup after OTP for unregistered users.
// Matches process-runner: POST /process/engagements/contact?taskId=…
// with a multipart body { contact: JSON.stringify(contact), file? }.
// --------------------------------------------------------------------------

export interface ProfileContact {
  firstName: string
  lastName: string
  fullName?: string
  email: string
  phone?: string
  role?: string
  note?: string
}

export async function updateSupplierContact(
  taskId: string,
  contact: ProfileContact,
  file?: File,
): Promise<unknown> {
  const fd = new FormData()
  fd.append(
    'contact',
    JSON.stringify({ ...contact, fullName: contact.fullName ?? `${contact.firstName} ${contact.lastName}`.trim() }),
  )
  if (file) fd.append('file', file)
  return axios
    .post(
      `${ENV.process}/engagements/contact?taskId=${encodeURIComponent(taskId)}`,
      fd,
      { withCredentials: true, cache: false },
    )
    .then((res) => res.data)
}

/** Mirrors process-runner `GET ${BUYER}/auth/users/me` via `getCurrentUserApi`. */
export async function getCurrentUserApi(
  opts?: AuthRequestOpts,
): Promise<SignedUserDetails> {
  return axios
    .get<SignedUserDetails>(`${BUYER()}/auth/users/me`, {
      headers: JSON_HEADERS,
      withCredentials: true,
      skipAuthRedirect: opts?.skipAuthRedirect,
    })
    .then((res) => res.data)
}

function resolveLogoUrl(logo: unknown): string | undefined {
  if (!logo) return undefined
  if (typeof logo === 'string') return logo
  if (typeof logo !== 'object') return undefined
  const meta = logo as {
    path?: string
    metadata?: Array<{ path?: string; size?: string }>
  }
  if (meta.path) {
    return meta.path.startsWith('http')
      ? meta.path
      : `${ENV.assets}/${meta.path.replace(/^\//, '')}`
  }
  const large = meta.metadata?.find((m) => m.size === 'large') ?? meta.metadata?.[0]
  if (large?.path) {
    return large.path.startsWith('http')
      ? large.path
      : `${ENV.assets}/${large.path.replace(/^\//, '')}`
  }
  return undefined
}

function mapTenantDetails(data: Record<string, unknown>): TenantDetails {
  const additional = data.additionalLogos as Record<string, unknown> | undefined
  let additionalLogos: { dark?: string; light?: string } | undefined
  if (additional && typeof additional === 'object') {
    const dark = resolveLogoUrl(additional.dark)
    const light = resolveLogoUrl(additional.light)
    if (dark || light) additionalLogos = { dark, light }
  }

  // Backend spells this field `additionaLogoBackgroundColor` (sic).
  // Accept either spelling on input and normalize on output.
  const rawBgColor =
    (data.additionalLogoBackgroundColor as
      | { dark?: string; light?: string }
      | undefined) ??
    (data.additionaLogoBackgroundColor as
      | { dark?: string; light?: string }
      | undefined)
  let additionalLogoBackgroundColor: { dark?: string; light?: string } | undefined
  if (rawBgColor && typeof rawBgColor === 'object') {
    additionalLogoBackgroundColor = {
      dark: rawBgColor.dark || undefined,
      light: rawBgColor.light || undefined,
    }
  }

  return {
    accountName: (data.accountName as string) || undefined,
    companyName: (data.companyName as string) || undefined,
    email: (data.email as string) || undefined,
    tenantId: (data.tenantId as string) || '',
    tenantExternalName: (data.tenantExternalName as string) || undefined,
    logo: resolveLogoUrl(data.logo),
    logoBackgroundColor: (data.logoBackgroundColor as string) || undefined,
    additionalLogos,
    additionalLogoBackgroundColor,
    banner: resolveLogoUrl(data.banner),
    sandbox: Boolean(data.sandbox),
  }
}

export async function getTenantDetailsApi(
  tenantId: string,
  opts?: AuthRequestOpts,
): Promise<TenantDetails> {
  return axios
    .get(`${BUYER()}/auth/tenants/${encodeURIComponent(tenantId)}`, {
      headers: JSON_HEADERS,
      withCredentials: true,
      skipAuthRedirect: opts?.skipAuthRedirect,
    })
    .then((res) => mapTenantDetails((res.data ?? {}) as Record<string, unknown>))
}
